/**
 * Learning (WALLIYO-0131), as the mockup draws it: what is required of you,
 * the catalogue, and — for HR — how far each team has got.
 *
 * The connection worth keeping is that a course can be required *by* a
 * permission, and then it is finished before the grant rather than after. The
 * row says which permission, because "required" with no reason attached is a
 * thing people work around.
 */

import type { Course, Enrolment, TeamLearning } from "@wallyo/contracts/console/hr";
import { settingName, t, type MessageId } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";

export type LearningState =
  | { status: "loading"; data: LearningData | null }
  | { status: "ready"; data: LearningData }
  | { status: "failed"; data: LearningData | null; error: unknown };

export type LearningData = {
  courses: readonly Course[];
  mine: readonly Enrolment[];
  byTeam: readonly TeamLearning[];
};

const STATUS_LABEL: Record<Enrolment["status"], MessageId> = {
  enrolled: "learning.status.enrolled",
  in_progress: "learning.status.inProgress",
  done: "learning.status.done",
};

const STATUS_TONE: Record<Enrolment["status"], string> = {
  enrolled: "pill",
  in_progress: "pill warn",
  done: "pill ok",
};

/** Why a course is required, said rather than left implied. */
function Required({ course }: { course: Course }) {
  if (course.requiredForSettingId) {
    return (
      <span className="foot">{t("learning.requiredBy", { permission: settingName(course.requiredForSettingId) })}</span>
    );
  }
  if (course.requiredOfEverybody) return <span className="pill warn">{t("learning.requiredOfAll")}</span>;
  return <span className="foot">{course.audience ?? t("learning.anybody")}</span>;
}

export function LearningView(props: {
  state: LearningState;
  onAddCourse: () => void;
  onEnrol: (course: Course, progress: number) => void;
  onRetry: () => void;
}) {
  const { state } = props;
  const data = state.data;
  const enrolledIn = new Map((data?.mine ?? []).map((each) => [each.courseId, each]));

  return (
    <>
      <div className="hr-search">
        <div className="grow" />
        <button className="btn" type="button" onClick={props.onAddCourse} disabled={state.status !== "ready"}>
          {t("learning.addCourse")}
        </button>
      </div>

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!data && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {data ? (
        <>
          <section className="card">
            <h2>{t("learning.mine.title")}</h2>
            <p className="foot">{t("learning.mine.help")}</p>
            {data.mine.length === 0 ? (
              <p className="foot">{t("learning.mine.none")}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("learning.col.course")}</th>
                      <th>{t("learning.col.status")}</th>
                      <th className="num">{t("learning.col.progress")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.mine.map((enrolment) => {
                      const course = data.courses.find((each) => each.courseId === enrolment.courseId);
                      return (
                        <tr key={enrolment.courseEnrolmentId}>
                          <td>
                            <strong>{enrolment.title}</strong>
                            {course ? (
                              <div>
                                <Required course={course} />
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <span className={STATUS_TONE[enrolment.status]}>{t(STATUS_LABEL[enrolment.status])}</span>
                          </td>
                          <td className="num mono">{enrolment.progress}%</td>
                          <td className="num">
                            {enrolment.status === "done" || !course ? null : (
                              <div className="btns">
                                <button
                                  className="btn quiet small"
                                  type="button"
                                  onClick={() => props.onEnrol(course, Math.min(100, enrolment.progress + 25))}
                                >
                                  {t("learning.continue")}
                                </button>
                                <button className="btn small" type="button" onClick={() => props.onEnrol(course, 100)}>
                                  {t("learning.finish")}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card">
            <h2>{t("learning.catalogue.title")}</h2>
            <p className="foot">{t("learning.catalogue.help")}</p>
            <ul className="hr-plain">
              {data.courses
                .filter((course) => course.active)
                .map((course) => (
                  <li key={course.courseId} className="hr-search">
                    <div>
                      <strong>{course.title}</strong>
                      <div>
                        <Required course={course} />
                        {course.minutes ? (
                          <span className="foot"> · {t("learning.minutes", { minutes: String(course.minutes) })}</span>
                        ) : null}
                        <span className="foot"> · {t("learning.enrolled", { count: String(course.enrolled) })}</span>
                      </div>
                    </div>
                    <div className="grow" />
                    {enrolledIn.has(course.courseId) ? (
                      <span className="pill">{t("learning.alreadyEnrolled")}</span>
                    ) : (
                      <button className="btn quiet small" type="button" onClick={() => props.onEnrol(course, 0)}>
                        {t("learning.enrol")}
                      </button>
                    )}
                  </li>
                ))}
            </ul>
          </section>

          {/* HR's half. Marked, because it is on a tab everybody can open. */}
          {data.byTeam.length > 0 ? (
            <section className="card">
              <div className="hr-search">
                <h2>{t("learning.byTeam.title")}</h2>
                <div className="grow" />
                <span className="pill">🔒 {t("learning.hrOnly")}</span>
              </div>
              <p className="foot">{t("learning.byTeam.help")}</p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("learning.col.team")}</th>
                      <th className="num">{t("learning.col.people")}</th>
                      <th className="num">{t("learning.col.done")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byTeam.map((team) => (
                      <tr key={team.staffTeamId}>
                        <td>{team.name}</td>
                        <td className="num">{team.people}</td>
                        <td className="num">
                          {team.done === team.people ? (
                            <span className="pill ok">{team.done}</span>
                          ) : (
                            <span className="pill warn">{team.done}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}
